import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import {
    EnumGlobalRoutes,
    ROUTE_TITLES_CONSTANT,
} from '@/app/router/routerConstants'
import SearchHeader from '@/compoments/common/SearchHeader'
import { selectNotificationList } from '@/features/notifications/notificationsSlice'
import { NotificationDropdownMenu } from '@/pages/Dispatcher/NotificationManagerPage/components/NotificationMenu'
import { useLazyLogoutQuery } from '@/services/auth'
import { useGetMyNotificationQuery } from '@/services/notifications/notifications'
import type { RootState } from '@/store'
import type { User, UserRoleEnum } from '@/types'
import { ROLE_LABELS } from '@/types'
import { getMediaUrl } from '@/utils/helpers'

import BellSvg from '../../assets/img/ico-bell.svg?react'
import ChevronDownIcon from '../../assets/img/ico-chevro-down.svg?react'
import ChevronPrevIcon from '../../assets/img/ico-chevron-prev.svg?react'
import LunaThemeSvg from '../../assets/img/ico-luna-theme.svg?react'
import SunThemeSvg from '../../assets/img/ico-sun-theme.svg?react'
import useModal from '../../hooks/useModal'
import Dropdown from '../common/Dropdown'
import SelectActiveRoleModal, {
    ALLOWED_ROLES,
} from '../modals/SelectActiveRoleModal'
import { baseApi } from '@/services/baseApi'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import Button from '../common/Button'

const NotificationDropdownButton = ({ incoming }: { incoming: boolean }) => (
    <div className="ico relative">
        {incoming && <span className="incoming-notification"></span>}
        <BellSvg />
    </div>
)

const ProfileDropdownButton = ({ user }: { user: User }) => (
    <>
        <div className="profile-info">
            <div className="ico-avatar btn btn--xl btn--square">
                <img
                    src={getMediaUrl(user.photo)}
                    alt={user.fullName}
                />
            </div>
            <div className="profile-initials">
                <p className="profile-name">{user.fullName}</p>
                <p className="profile-role">
                    {ROLE_LABELS[user.role as UserRoleEnum] || 'Dispatcher'}
                </p>
            </div>
        </div>
        <div className="ico ico-profile-arrow-drp">
            <ChevronDownIcon />
        </div>
    </>
)

const ProfileDropdownMenu = ({
    onLogout,
    openModal,
}: {
    onLogout: () => void
    openModal: (type: string) => void
}) => {
    const { user } = useAppSelector(state => state.auth)
    const roles: UserRoleEnum[] = Array.isArray(user?.roles)
        ? (user.roles as UserRoleEnum[])
        : []

    const filteredRoles = roles.filter(role => ALLOWED_ROLES.includes(role))

    const links = [
        {
            name: 'Account Settings',
            href: EnumGlobalRoutes.SETTINGS + '?viewTab=account-settings',
        },
        // {
        //     name: 'Application Preferences',
        //     href: EnumGlobalRoutes.SETTINGS,
        // },
        {
            name: 'Notification Settings',
            href: EnumGlobalRoutes.SETTINGS + '?viewTab=notification-settings',
        },
        {
            name: 'Security Settings',
            href: EnumGlobalRoutes.SETTINGS + '?viewTab=security-settings',
        },
        {
            name: 'Switch Account',
            href: EnumGlobalRoutes.DASHBOARD,
            hide: filteredRoles.length === 1,
            onClick: () => openModal('add-role'),
        },
    ]

    return (
        <ul className="dropdown-list-links">
            {links
                .filter(link => !link.hide)
                .map((link, index) => (
                    <li
                        key={index}
                        className="dropdown-list-link"
                    >
                        <Link
                            to={link.href}
                            onClick={link?.onClick}
                            className="btn btn--md text-left btn-drp-link justify-start"
                        >
                            <span>{link.name}</span>
                        </Link>
                    </li>
                ))}
            <li className="dropdown-list-link px-3">
                <hr />
            </li>
            <li className="dropdown-list-link">
                <button
                    onClick={onLogout}
                    className="btn btn--md text-left btn-drp-link justify-start"
                >
                    <span>Log Out</span>
                </button>
            </li>
        </ul>
    )
}

const Header = () => {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { isOpen, closeModal, openModal } = useModal()
    const [isDarkTheme, setIsDarkTheme] = useState(false)
    const { activeRole, user } = useSelector((state: RootState) => state.auth)
    const dispatch = useAppDispatch()
    const [triggerLogout] = useLazyLogoutQuery()
    const notificationList = useSelector(selectNotificationList)
    useGetMyNotificationQuery('')

    useEffect(() => {
        const savedTheme = localStorage.getItem('fsm-theme') || 'light-theme'
        document.querySelector('body')?.classList.add(savedTheme)
        setIsDarkTheme(savedTheme === 'dark-theme')
    }, [])

    const handleToggleTheme = () => {
        const body = document.querySelector('body')
        const newTheme = isDarkTheme ? 'light-theme' : 'dark-theme'

        body?.classList.remove(isDarkTheme ? 'dark-theme' : 'light-theme')
        body?.classList.add(newTheme)
        localStorage.setItem('fsm-theme', newTheme)
        setIsDarkTheme(!isDarkTheme)
    }

    const handleLogout = () => {
        triggerLogout()
        dispatch(baseApi.util.resetApiState())
        navigate(EnumGlobalRoutes.LOGIN)
        // window.location.reload();
    }

    return (
        <header className="header sidebar-indentation">
            <div className="header-container px-container">
                <div className="header-option">
                    {pathname.split('/').length > 2 && (
                        <Link
                            to={'/' + pathname.split('/')[1]}
                            className="btn btn--xl btn--square full-radius btn-shadow-iset"
                        >
                            <ChevronPrevIcon />
                        </Link>
                    )}
                    {/* <Button
                        variant="default"
                        size='xl'
                        square
                        className='full-radius btn-shadow-iset' 
                        onClick={() => navigate(-1)}   
                    ><ChevronPrevIcon /></Button> */}
                    <h4 className="heading page-title">
                        {ROUTE_TITLES_CONSTANT[
                            pathname as keyof typeof ROUTE_TITLES_CONSTANT
                        ] || 'Back'}
                    </h4>
                </div>
                <div className="header-tools-list">
                    <div className="header-tool-item flex-auto justify-end">
                        <div className="header-tool-item--sm flex-auto justify-end">
                            <SearchHeader />
                            <Dropdown
                                buttonClassName={'btn btn-tool'}
                                closeSelf={true}
                                button={
                                    <NotificationDropdownButton
                                        incoming={notificationList.length > 0}
                                    />
                                }
                                menuClassName={
                                    'dropdown__body dropdown-notifications'
                                }
                                menu={<NotificationDropdownMenu />}
                            />
                        </div>
                        <div className="switch-control">
                            <input
                                type="checkbox"
                                hidden
                                className="theme-switch"
                                name="theme-color"
                                id="theme-toggle"
                                checked={isDarkTheme}
                                readOnly
                                aria-hidden="true" /* Hidden input is for state only, not user interaction */
                            />
                            <div
                                className="switch-btn"
                                role="switch"
                                aria-checked={isDarkTheme}
                                tabIndex={0}
                                onClick={handleToggleTheme}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        handleToggleTheme()
                                    }
                                }}
                            >
                                <div
                                    className={`switch-point-wrapp ${
                                        isDarkTheme ? 'dark' : 'light'
                                    }`}
                                >
                                    <span className="switch-point"></span>
                                </div>
                                <div className="bg-switch-wrapper">
                                    <span className="ico">
                                        <LunaThemeSvg />
                                    </span>
                                    <span className="ico">
                                        <SunThemeSvg />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="header-tool-item">
                        <Dropdown
                            buttonClassName={
                                'btn dropdown-profile-btn btn btn--xl'
                            }
                            button={
                                <ProfileDropdownButton
                                    user={{ ...user, role: activeRole } as User}
                                />
                            }
                            menuClassName={
                                'dropdown__body dropdown-profile-body'
                            }
                            menu={
                                <ProfileDropdownMenu
                                    onLogout={handleLogout}
                                    openModal={openModal}
                                />
                            }
                        />
                    </div>
                </div>
            </div>
            <SelectActiveRoleModal
                isOpen={isOpen('add-role')}
                onClose={() => closeModal('add-role')}
            />
        </header>
    )
}

export default Header
